import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { CMSPageDetails } from "../services/website.services";
import { capitalizeFirstLetter, getLocalKey, setTitle } from "../../../utils/commonfunction";
import { FiHome, FiClock, FiShield, FiRefreshCw } from "react-icons/fi";
import ComingSoon from "./ComingSoon";
import Page404 from "./Page404";

const SLUG_METADATA = {
  about: { title: "About AstroAdi", badge: "About AstroAdi" },
  "about-us": { title: "About AstroAdi", badge: "About AstroAdi" },
  "user-about-us": { title: "About AstroAdi", badge: "About AstroAdi" },
  "about-astrologer-portal": { title: "About Us", badge: "About Us" },
  privacy: { title: "Privacy Policy", badge: "Privacy & Policy" },
  "privacy-policy": { title: "Privacy Policy", badge: "Privacy & Policy" },
  "user-privacy-policy": { title: "Privacy Policy", badge: "Privacy & Policy" },
  terms: { title: "Terms & Conditions", badge: "Legal Terms" },
  "terms-conditions": { title: "Terms & Conditions", badge: "Legal Terms" },
  "terms-and-conditions": { title: "Terms & Conditions", badge: "Legal Terms" },
  "user-terms-service": { title: "Terms & Conditions", badge: "Legal Terms" },
  contact: { title: "Contact Us", badge: "Get In Touch" },
  "contact-us": { title: "Contact Us", badge: "Get In Touch" },
  refund: { title: "Refund Policy", badge: "Refund Policy" },
  "refund-policy": { title: "Refund Policy", badge: "Refund Policy" },
  disclaimer: { title: "Disclaimer", badge: "Legal Disclaimer" },
};

function DynamicWebPage({ defaultSlug }) {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const isWebView =
    location.pathname.startsWith("/org-info") ||
    searchParams.get("webview") === "true";

  const activeSlug = (
    defaultSlug ||
    params.slug ||
    searchParams.get("page_slug") ||
    ""
  ).toLowerCase().trim();

  const metaFallback = SLUG_METADATA[activeSlug] || {};
  const fallbackTitle =
    metaFallback.title ||
    capitalizeFirstLetter(activeSlug.replace(/[-_]+/g, " ")) ||
    "Page Information";
  const fallbackBadge = metaFallback.badge || "Astro Adi";

  const [CMSPageData, setCMSPageData] = useState({});
  const [apiLoaded, setApiLoaded] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [systemLang, setSystemLang] = useState(getLocalKey("system_language"));

  useEffect(() => {
    const handleLanguageChange = () => {
      const updatedLang = getLocalKey("system_language");
      setSystemLang(updatedLang);
    };

    document.addEventListener("language_change", handleLanguageChange);
    return () => {
      document.removeEventListener("language_change", handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    if (!activeSlug) {
      setApiLoaded(true);
      setIsNotFound(true);
      setHasApiError(false);
      return;
    }

    setApiLoaded(false);
    setIsNotFound(false);
    setHasApiError(false);
    window.scrollTo({ top: 0, behavior: "smooth" });

    CMSPageDetails(activeSlug)
      .then((response) => {
        setApiLoaded(true);
        if (response && response.data && typeof response.data === "object" && Object.keys(response.data).length > 0) {
          setCMSPageData(response.data);
          setIsNotFound(false);
          setHasApiError(false);
        } else {
          setCMSPageData({});
          setIsNotFound(true);
          setHasApiError(false);
        }
      })
      .catch((error) => {
        console.error("Error loading CMS page:", error);
        setApiLoaded(true);
        setCMSPageData({});
        setHasApiError(true);
      });
  }, [activeSlug, systemLang, retryCount]);

  const publicAboutSlugs = ["about", "about-us", "user-about-us"];
  const isAboutPage = publicAboutSlugs.includes(activeSlug);
  const displayTitle = isAboutPage
    ? "About AstroAdi"
    : CMSPageData?.title || fallbackTitle;

  useEffect(() => {
    if (displayTitle && !isNotFound && !hasApiError) {
      setTitle(`${displayTitle} | Astro Adi`);
    }
  }, [displayTitle, isNotFound, hasApiError]);

  // Loading Skeleton State
  if (!apiLoaded) {
    return (
      <div className="dynamic-page-wrapper bg-white">
        {/* Skeleton Hero Banner */}
        <section className={`relative bg-[url('../../images/banner.png')] bg-cover bg-center overflow-hidden text-center ${
          isWebView ? "!pt-[30px] !pb-[20px]" : "!pt-[78px] md:!pt-[88px] !pb-[16px] md:!pb-[22px]"
        }`}>
          <div className="absolute inset-0 bg-black/65 z-[1]"></div>
          <div className="container relative z-[2] mx-auto px-4">
            <div className="max-w-[700px] mx-auto flex flex-col items-center gap-1.5">
              <Skeleton height={20} width={90} className="rounded-full" />
              <Skeleton height={34} width={240} className="mt-1" />
            </div>
          </div>
        </section>

        {/* Skeleton Body Content */}
        <section className="pt-4 md:pt-6 pb-12 md:pb-16 bg-gray-50/70 relative min-h-[50vh]">
          <div className="container max-w-4xl mx-auto px-4 sm:px-6">
            <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg shadow-gray-200/50 border border-gray-100">
              <Skeleton height={28} width="35%" />
              <div className="space-y-3 mt-4">
                <Skeleton height={18} count={3} />
                <Skeleton height={18} width="85%" />
                <Skeleton height={100} className="rounded-xl mt-3" />
                <Skeleton height={18} count={4} className="mt-3" />
                <Skeleton height={18} width="60%" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // 1. API Network / Server Error -> Show friendly Connection Issue card with Retry
  if (hasApiError) {
    return (
      <div className="dynamic-page-wrapper bg-white">
        <section className={`relative bg-[url('../../images/banner.png')] bg-cover bg-center overflow-hidden text-center ${
          isWebView ? "!pt-[30px] !pb-[20px]" : "!pt-[78px] md:!pt-[88px] !pb-[16px] md:!pb-[22px]"
        }`}>
          <div className="absolute inset-0 bg-black/65 z-[1]"></div>
          <div className="container relative z-[2] mx-auto px-4">
            <h1 className="text-white text-2xl sm:text-3xl font-extrabold tracking-tight m-0 drop-shadow-md">
              Connection Issue
            </h1>
          </div>
        </section>

        <section className="pt-6 md:pt-8 pb-12 md:pb-16 bg-gray-50/70 relative">
          <div className="container max-w-lg mx-auto px-4 text-center">
            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl shadow-gray-200/60 border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 text-2xl">
                <FiRefreshCw />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Page</h3>
              <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                We encountered an issue connecting to the server. Please check your internet connection or click below to try again.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setRetryCount((prev) => prev + 1)}
                  className="custom-btn custom-btn-primary !px-6 !py-2.5 text-sm font-semibold flex items-center gap-2 shadow-md shadow-yellow-500/20"
                >
                  <FiRefreshCw />
                  <span>Try Again</span>
                </button>
                <Link
                  to="/"
                  className="px-6 py-2.5 rounded-full border border-gray-300 text-gray-700 hover:border-yellow-500 hover:text-yellow-600 text-sm font-medium transition-colors !no-underline"
                >
                  Return Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // 2. Slug not present in database -> Show 404 Page
  if (isNotFound) {
    return <Page404 />;
  }

  // 3. Page present in DB but content not filled yet -> Show Coming Soon Page
  if (!CMSPageData?.description) {
    return (
      <ComingSoon
        title={CMSPageData?.title || displayTitle}
        badge="COMING SOON"
        description={`The content for "${CMSPageData?.title || displayTitle}" is currently under active preparation and will be available in the upcoming release. Stay tuned as we update our platform with detailed information.`}
      />
    );
  }

  return (
    <div className="dynamic-page-wrapper bg-white">
      {/* ===== Hero Banner Section ===== */}
      <section className={`relative bg-[url('../../images/banner.png')] bg-cover bg-center overflow-hidden text-center ${
        isWebView ? "!pt-[30px] !pb-[20px]" : "!pt-[78px] md:!pt-[88px] !pb-[16px] md:!pb-[22px]"
      }`}>
        {/* Dark overlay for contrast */}
        <div className="absolute inset-0 bg-black/65 z-[1]"></div>

        <div className="container relative z-[2] mx-auto px-4">
          <div className="max-w-[700px] mx-auto flex flex-col items-center gap-1.5">
            {/* Golden Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-400/40 text-yellow-400 text-[11px] md:text-xs font-semibold tracking-wider uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse"></span>
              {fallbackBadge}
            </div>

            {/* Main Page Title */}
            <h1 className="web-h1 text-white text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight m-0 drop-shadow-md">
              {displayTitle}
            </h1>

            {/* Breadcrumb Navigation - hidden in mobile app WebView */}
            {!isWebView && (
              <nav className="inline-flex items-center gap-2 bg-black/40 backdrop-blur-md px-3.5 py-1 rounded-full border border-white/10 text-xs text-gray-300 mt-0.5">
                <Link
                  to="/"
                  className="text-gray-300 hover:text-yellow-400 no-underline transition-colors flex items-center gap-1"
                >
                  <FiHome className="text-xs" />
                  <span>Home</span>
                </Link>
                <span className="text-gray-500">/</span>
                <span className="text-yellow-400 font-medium">{displayTitle}</span>
              </nav>
            )}
          </div>
        </div>
      </section>

      {/* ===== Page Content Body ===== */}
      <section className="pt-4 md:pt-6 pb-12 md:pb-16 bg-gray-50/70 relative min-h-[50vh] overflow-hidden">
        {/* Subtle decorative background shapes */}
        <div className="absolute top-[10%] right-0 hidden 2xl:block opacity-40 pointer-events-none">
          <img src="/images/shape1.png" alt="" />
        </div>
        <div className="absolute bottom-[10%] left-0 hidden 2xl:block opacity-40 pointer-events-none">
          <img src="/images/shape2.png" alt="" />
        </div>

        <div className="container max-w-4xl mx-auto px-4 sm:px-6 relative z-[2]">
            {/* Loaded CMS Content Card */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-12 shadow-lg shadow-gray-200/50 border border-gray-100">
              {/* Header meta inside card */}
              <div className="flex flex-wrap items-center justify-between pb-4 mb-6 border-b border-gray-100 gap-2">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <FiShield className="text-yellow-600 text-sm" />
                  <span>
                    {isAboutPage
                      ? "The story behind AstroAdi"
                      : "Verified Astro Adi Official Document"}
                  </span>
                </div>
                {CMSPageData?.updatedAt && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <FiClock />
                    <span>
                      Updated: {new Date(CMSPageData.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Short Description Highlight Callout */}
              {CMSPageData?.short_description && (
                <div className="mb-8 p-5 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50/30 border-l-4 border-[#DEA916] text-gray-800 text-base md:text-lg font-medium leading-relaxed shadow-sm">
                  {CMSPageData.short_description}
                </div>
              )}

              {/* Formatted HTML Content */}
              <div
                className="dynamic-cms-prose"
                dangerouslySetInnerHTML={{ __html: CMSPageData.description }}
              />

              {/* Footer assistance box */}
              <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600 bg-gray-50/80 rounded-2xl p-5">
                <div>
                  <span className="font-semibold text-gray-900 block mb-0.5">
                    {isAboutPage
                      ? "Ready to begin your journey?"
                      : "Have questions about this policy?"}
                  </span>
                  <span>
                    {isAboutPage
                      ? "Explore astrology, spiritual guidance and wellness in one place."
                      : "Our support team is available 24/7 to assist you."}
                  </span>
                </div>
                <Link
                  to={isAboutPage ? "/download-app" : "/contact"}
                  className="custom-btn custom-btn-primary !px-5 !py-2.5 text-xs font-semibold whitespace-nowrap !no-underline"
                >
                  {isAboutPage ? "Download AstroAdi" : "Contact Support"}
                </Link>
              </div>
            </div>
        </div>
      </section>

      {/* Embedded styles for CMS prose content */}
      <style>{`
        .dynamic-cms-prose {
          color: #3f3f46;
          font-size: 1.05rem;
          line-height: 1.8;
        }
        .dynamic-cms-prose p {
          margin-bottom: 1.25rem;
          color: #4b5563;
          line-height: 1.8;
        }
        .dynamic-cms-prose h2,
        .dynamic-cms-prose h3,
        .dynamic-cms-prose h4 {
          color: #111827;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.85rem;
          letter-spacing: -0.01em;
        }
        .dynamic-cms-prose h2 {
          font-size: 1.65rem;
          border-bottom: 1px solid #f3f4f6;
          padding-bottom: 0.5rem;
        }
        .dynamic-cms-prose h3 {
          font-size: 1.35rem;
          position: relative;
          padding-left: 0.85rem;
        }
        .dynamic-cms-prose h3::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.35em;
          bottom: 0.2em;
          width: 3px;
          border-radius: 999px;
          background: #dea916;
        }
        .dynamic-cms-prose h4 {
          font-size: 1.15rem;
        }
        .dynamic-cms-prose strong,
        .dynamic-cms-prose b {
          color: #18181b;
          font-weight: 600;
        }
        .dynamic-cms-prose ul,
        .dynamic-cms-prose ol {
          margin-top: 1rem;
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        .dynamic-cms-prose ul {
          list-style-type: disc;
        }
        .dynamic-cms-prose ol {
          list-style-type: decimal;
        }
        .dynamic-cms-prose li {
          margin-bottom: 0.65rem;
          color: #4b5563;
        }
        .dynamic-cms-prose a {
          color: #d97706;
          text-decoration: underline;
          font-weight: 500;
        }
        .dynamic-cms-prose a:hover {
          color: #b45309;
        }
        .dynamic-cms-prose blockquote {
          border-left: 4px solid #dea916;
          background: linear-gradient(90deg, #fffbeb 0%, #fff7ed 100%);
          padding: 1.15rem 1.4rem;
          border-radius: 0 0.75rem 0.75rem 0;
          margin: 2rem 0 0.5rem;
          font-style: italic;
          font-size: 1.2rem;
          font-weight: 600;
          color: #78350f;
          text-align: center;
        }
      `}</style>
    </div>
  );
}

export default DynamicWebPage;
